import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { AxiosError } from "axios";
import {
  MePaymentVerificationsApi,
  OrderItemType,
  type OrderPaymentDetails,
  OrderPaymentsApi,
  type V2MeVerificationsPaymentPostRequest,
  VatNumberCheckStatus,
} from "mws-ts-v2-sdk";

import { getUniversalAxios } from "~/utils/axios/getUniversalAxios";

let paymentsApi: OrderPaymentsApi;
let v2MePaymentVerificationsApi: MePaymentVerificationsApi;
const getPaymentsApi = () => {
  if (!paymentsApi) {
    paymentsApi = new OrderPaymentsApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
      getUniversalAxios(),
    );
  }

  return paymentsApi;
};

const getMePaymentVerificationsApi = () => {
  // reduce memory overhead by only using one instance
  if (!v2MePaymentVerificationsApi) {
    v2MePaymentVerificationsApi = new MePaymentVerificationsApi(
      undefined,
      useRuntimeConfig().public.financeServiceUrl,
      getUniversalAxios(),
    );
  }

  return v2MePaymentVerificationsApi;
};

export const paymentsQueryKeys = createQueryKeys("payments", {
  details: (payload: {
    platformId: MaybeRef<string>;
    orderId: MaybeRef<string>;
    filters?: MaybeRef<{
      paymentMethod: string | null;
      shippingAddressId?: string;
    }>;
  }) => ({
    queryKey: [payload.orderId, payload.filters],
    queryFn: async () => {
      const unreffedFilters = unref(payload.filters);

      const response = await getPaymentsApi().v2OrdersOrderIdPaymentDetailsGet({
        orderId: unref(payload.orderId),
        xPlatformKey: unref(payload.platformId),
        paymentMethod: unreffedFilters?.paymentMethod ?? undefined,
        shippingAddressId: unreffedFilters?.shippingAddressId,
      });

      return response.data;
    },
  }),
});

export const usePaymentDetailsQuery = (
  payload: {
    orderId: Ref<string>;
    filters: MaybeRef<{
      paymentMethod: string | null;
      shippingAddressId?: string;
    }>;
  },
  options?: {
    enabled?: MaybeRef<boolean>;
    placeholderData?: (data?: OrderPaymentDetails) => typeof data;
  },
) => {
  const router = useRouter();
  const localePath = useLocalePath();
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const isHandleableError = (
    error: AxiosError,
  ): error is Required<AxiosError> => {
    const errorCode = error.response?.status;

    return Boolean(errorCode && (errorCode === 401 || errorCode === 403));
  };

  const paymentDetails = useQuery({
    ...paymentsQueryKeys.details({
      orderId: payload.orderId,
      filters: payload.filters,
      platformId,
    }),
    ...options,
    retry: (failureCount, error: AxiosError) => {
      // Don't retry 401s and 403s. Those need to get redirected.
      if (isHandleableError(error)) {
        return false;
      }

      return failureCount < 2;
    },
    refetchInterval: (query) => {
      // Start polling the query if there's a frame without an image.
      const frameInOrderWithoutImage = query.state.data?.orderItems.find(
        (orderItem) =>
          orderItem.orderItemType === OrderItemType.Frame &&
          !orderItem.productReference?.mainImageUrl,
      );

      // Start polling if we're checking the VAT number
      const vatNumberIsChecking =
        query.state.data?.vatNumberCheckStatus ===
        VatNumberCheckStatus.NotChecked;

      return frameInOrderWithoutImage || vatNumberIsChecking ? 3000 : false;
    },
  });

  watchEffect(() => {
    const error = paymentDetails.error.value;

    if (error && isHandleableError(error)) {
      if (error.response.status === 401) {
        router.replace(localePath({ path: "/login" }));
      } else {
        router.replace(localePath({ path: "/my-account/my-orders" }));
      }
    }
  });

  return paymentDetails;
};

export const useVerificationPaymentMutation = () => {
  return useMutation({
    mutationFn: async (request: V2MeVerificationsPaymentPostRequest) => {
      const response =
        await getMePaymentVerificationsApi().v2MeVerificationsPaymentPost({
          v2MeVerificationsPaymentPostRequest: request,
        });

      return response.data;
    },
  });
};
